/**
 * Pure geometry for the Refine-page marquee selection.
 *
 * The marquee is drawn in CSS pixels over RefineStage's fixed 3:2 frame. The
 * <img> inside that frame is `absolute inset-0 h-full w-full`, so its content
 * box IS the frame box, and `object-fit` places the natural image inside it
 * with the default `object-position: 50% 50%`. Converting a drawn rect back to
 * source-image coordinates is therefore the same inverse transform for both
 * fits, differing only in whether the scale is chosen by `max` or `min`.
 *
 * Everything here is CSS pixels and ratios, so devicePixelRatio never enters.
 */

export interface Point {
  x: number;
  y: number;
}

/** A rect in CSS pixels, relative to the stage frame's top-left. */
export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A rect in source-image coordinates, each component in 0..1. */
export interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type ObjectFit = "cover" | "contain";

/** Gemini needs real pixels to work from; below this a selection is mostly
 *  upscaling noise. ~5% of the 1264px generated width. */
export const MIN_SELECTION_SOURCE_PX = 64;

/** Users consistently under-draw and clip feet, legs and casters. Expanding by
 *  6% of the rect's own size recovers a leg tip without dragging in a
 *  neighbouring object. */
export const SELECTION_PADDING = 0.06;

export const SELECTION_TOO_SMALL_MESSAGE =
  "Drag a larger area — this selection is too small to turn into a design.";

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Where and how big the natural image is drawn inside the frame. Offsets are
 *  >= 0 when letterboxed (contain) and <= 0 when overflowing (cover). */
function fitScaleAndOffset(
  frame: Size,
  natural: Size,
  fit: ObjectFit,
): { displayed: Size; offsetX: number; offsetY: number } {
  const scaleX = frame.width / natural.width;
  const scaleY = frame.height / natural.height;
  const scale =
    fit === "cover" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
  const displayed = {
    width: natural.width * scale,
    height: natural.height * scale,
  };
  return {
    displayed,
    offsetX: (frame.width - displayed.width) / 2,
    offsetY: (frame.height - displayed.height) / 2,
  };
}

/** Build a rect from two drag corners, so either drag direction works. */
export function rectFromPoints(a: Point, b: Point): PixelRect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/**
 * Convert a rect drawn over the stage frame into normalized source-image
 * coordinates, clamped to the image (under `contain` the user can drag across
 * the letterbox bars).
 *
 * Returns null when the inputs are degenerate or when the rect lands entirely
 * outside the image, leaving nothing to crop.
 */
export function frameRectToNormalized(
  rect: PixelRect,
  frame: Size,
  natural: Size,
  fit: ObjectFit,
): NormalizedRect | null {
  if (
    frame.width <= 0 ||
    frame.height <= 0 ||
    natural.width <= 0 ||
    natural.height <= 0
  ) {
    return null;
  }

  const { displayed, offsetX, offsetY } = fitScaleAndOffset(
    frame,
    natural,
    fit,
  );

  // u = (px - offsetX) / displayedWidth collapses the divide-by-scale and the
  // divide-by-natural-width into one step.
  const left = clamp01((rect.x - offsetX) / displayed.width);
  const right = clamp01((rect.x + rect.width - offsetX) / displayed.width);
  const top = clamp01((rect.y - offsetY) / displayed.height);
  const bottom = clamp01((rect.y + rect.height - offsetY) / displayed.height);

  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return null;

  return { x: left, y: top, width, height };
}

/** Expand a rect by `pad` of its own size on each side, clamped to the image. */
export function padNormalizedRect(
  rect: NormalizedRect,
  pad: number = SELECTION_PADDING,
): NormalizedRect {
  const left = clamp01(rect.x - rect.width * pad);
  const right = clamp01(rect.x + rect.width * (1 + pad));
  const top = clamp01(rect.y - rect.height * pad);
  const bottom = clamp01(rect.y + rect.height * (1 + pad));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** The selection's size in source-image pixels. */
export function selectionPixelSize(rect: NormalizedRect, natural: Size): Size {
  return {
    width: rect.width * natural.width,
    height: rect.height * natural.height,
  };
}

/** House convention: `true` when valid, otherwise the message to show. */
export function validateSelection(
  rect: NormalizedRect,
  natural: Size,
): true | string {
  const { width, height } = selectionPixelSize(rect, natural);
  if (width < MIN_SELECTION_SOURCE_PX || height < MIN_SELECTION_SOURCE_PX) {
    return SELECTION_TOO_SMALL_MESSAGE;
  }
  return true;
}
